import type { ProjectBrief } from '@yingbaba/types';

export const briefFields = [
  ['product', '品牌与产品', '品牌背景、产品特点，以及本次要解决的问题'],
  ['audience', '目标人群', '面向谁？他们关注什么，通常在哪里看到内容？'],
  ['objective', '营销目的', '例如提升认知、介绍新品、获取咨询、促成购买'],
  ['key_message', '表达内容', '希望观众记住什么？哪些信息必须出现？'],
  ['channels', '传播渠道', '例如视频号、抖音、官网、展会大屏'],
  ['deliverables', '交付内容与规格', '成片数量、横竖版、分辨率、字幕、源文件等'],
  ['style', '参考风格', '参考作品、节奏、视觉风格与不希望出现的表达'],
  ['constraints', '制作要求', '必拍内容、品牌规范、拍摄限制与注意事项'],
  ['decision_maker', '确认负责人', '负责反馈和确认的人员或角色'],
] as const;

export type BriefImportPlan = {
  brief: ProjectBrief;
  deadline: string;
  changes: { label: string; value: string }[];
};
// Build a reviewable fill-only plan; source data never supplies rights states,
// license references, evidence, duration or other project-only fields.
export function planBriefImport(
  current: ProjectBrief,
  deadline: string,
  raw: string,
): BriefImportPlan {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    throw new Error('这份草稿暂时无法读取，请选择其他版本。');
  }
  if (
    !value ||
    typeof value !== 'object' ||
    Array.isArray(value) ||
    (value as Record<string, unknown>).schema_version !== 1
  )
    throw new Error('这份草稿格式暂不支持，请在 AI 创作中重新保存需求后再试。');
  const source = value as Record<string, unknown>;
  const result: BriefImportPlan = { brief: { ...current }, deadline, changes: [] };
  for (const [key, label] of briefFields) {
    const text = source[key];
    if (text !== undefined && typeof text !== 'string')
      throw new Error(`草稿的“${label}”格式不正确，请先核对。`);
    if (typeof text !== 'string' || !text.trim()) continue;
    if ([...text.trim()].length > 4000) throw new Error(`草稿的“${label}”内容过长，请先精简。`);
    if (!current[key]?.trim()) {
      result.brief[key] = text.trim();
      result.changes.push({ label, value: text.trim() });
    }
  }
  const budget = source.budget_minor;
  if (
    budget !== undefined &&
    (!Number.isSafeInteger(budget) || Number(budget) < 0 || Number(budget) > 100_000_000_000)
  )
    throw new Error('草稿的计划预算无效，请先核对金额。');
  if (!current.budget_minor && typeof budget === 'number' && budget > 0) {
    result.brief.budget_minor = budget;
    result.changes.push({ label: '计划预算', value: `¥${(budget / 100).toFixed(2)}` });
  }
  const due = source.deadline;
  if (due !== undefined && typeof due !== 'string')
    throw new Error('草稿的交付日期格式不正确，请先核对。');
  if (typeof due === 'string' && due.trim()) {
    const v = due.trim(),
      d = new Date(`${v}T00:00:00Z`);
    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(v) ||
      !Number.isFinite(d.getTime()) ||
      d.toISOString().slice(0, 10) !== v
    )
      throw new Error('草稿的交付日期无效，请先核对。');
    if (!deadline.trim()) {
      result.deadline = v;
      result.changes.push({ label: '交付日期', value: v });
    }
  }
  const questions = source.open_questions;
  if (
    questions !== undefined &&
    questions !== null &&
    (!Array.isArray(questions) ||
      questions.length > 30 ||
      questions.some((q) => typeof q !== 'string' || [...q.trim()].length > 500))
  )
    throw new Error('草稿的待确认事项格式不正确，请先核对。');
  if (Array.isArray(questions) && !current.open_questions.some((q) => q.trim())) {
    const values = [...new Set((questions as string[]).map((q) => q.trim()).filter(Boolean))];
    if (values.length) {
      result.brief.open_questions = values;
      result.changes.push({ label: '待确认事项', value: values.join('\n') });
    }
  }
  return result;
}
